import React from 'react';
import { Controller } from 'react-hook-form';
import NumberFormat, { NumberFormatProps, NumberFormatValues, SourceInfo } from 'react-number-format';
import { Control } from 'react-hook-form/dist/types/form';
import { TextField, TextFieldProps } from '@mui/material';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';

interface Props<FormValues extends FieldValues = FieldValues> extends Omit<
  NumberFormatProps,
  'value' | 'onChange' | 'onValueChange'
> {
  label?: string;
  control: Control<FormValues>;
  textFieldProps?: TextFieldProps;
  name: UseControllerProps<FormValues>['name'];
  customOnChange?: (nextValue: number, name: UseControllerProps<FormValues>['name']) => void;
}

export const InterestInputField = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  customOnChange,
  textFieldProps = {},
  ...numberFormatProps
}: Props<FormValues>) => {
  const handleChange = (onFieldChange: any, { value }: NumberFormatValues, sourceInfo: SourceInfo) => {
    const parsedValue = parseFloat(value);

    if (sourceInfo.source === 'event') {
      if (customOnChange) {
        customOnChange(isNaN(parsedValue) ? 0 : parsedValue, name);
      } else {
        if (isNaN(parsedValue)) return onFieldChange(null);
        onFieldChange(parsedValue);
      }
    } else {
      onFieldChange(isNaN(parsedValue) ? null : parsedValue);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, value, onChange, ...field }, fieldState }) => (
        <NumberFormat
          suffix="%"
          fullWidth
          size="small"
          label={label}
          allowNegative
          inputRef={ref}
          decimalScale={2}
          thousandSeparator
          fixedDecimalScale
          variant="standard"
          customInput={TextField}
          {...field}
          {...(textFieldProps as any)}
          {...numberFormatProps}
          error={!!fieldState.error}
          helperText={fieldState.error ? fieldState.error.message : ''}
          value={parseFloat(value)}
          onValueChange={(values, sourceInfo) => handleChange(onChange, values, sourceInfo)}
        />
      )}
    />
  );
};
